import React, { useState, useEffect } from 'react';
import { placesAPI } from '../services/api';
import { MapPin, Plus, Edit2, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';

function PlaceFormModal({ place, onClose, onSaved }) {
  const isEdit = !!place;
  const [form, setForm] = useState({
    name: place?.name || '', code: place?.code || '',
    place_type: place?.place_type || 'offline',
    address: place?.address || '', phone: place?.phone || '',
    is_active: place?.is_active ?? true,
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error('Введите название'); return; }
    setLoading(true);
    try {
      if (isEdit) { await placesAPI.update(place.id, form); toast.success('Сохранено'); }
      else { await placesAPI.create(form); toast.success('Создано'); }
      onSaved(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Ошибка'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <h2 className="modal-title">{isEdit ? 'Редактировать' : 'Новая точка продаж'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Название *</label>
            <input className="form-input" required value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Магазин на Тверской" />
          </div>
          <div className="form-group">
            <label className="form-label">Код (для API)</label>
            <input className="form-input" value={form.code}
              onChange={e => setForm({ ...form, code: e.target.value })} placeholder="tverskaya" />
            <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>Используется при создании заказов через API: place_code</div>
          </div>
          <div className="form-group">
            <label className="form-label">Тип точки</label>
            <select className="form-input" value={form.place_type} onChange={e => setForm({ ...form, place_type: e.target.value })}>
              <option value="offline">Розничный магазин</option>
              <option value="crm">CRM (Битрикс24)</option>
              <option value="import">Исторический импорт</option>
              <option value="website">Сайт</option>
              <option value="app">Приложение</option>
              <option value="marketplace">Маркетплейс</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Адрес</label>
            <input className="form-input" value={form.address}
              onChange={e => setForm({ ...form, address: e.target.value })} placeholder="г. Москва, ул. Тверская, д. 1" />
          </div>
          <div className="form-group">
            <label className="form-label">Телефон</label>
            <input className="form-input" value={form.phone}
              onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+7 (495) 123-45-67" />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 16 }}>
            <input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} />
            <span style={{ fontSize: 13 }}>Активна</span>
          </label>
          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Отмена</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? '...' : isEdit ? 'Сохранить' : 'Создать'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function PlacesPage() {
  const [places, setPlaces] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingPlace, setEditingPlace] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => { placesAPI.list().then(res => setPlaces(res.data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить точку продаж?')) return;
    try { await placesAPI.delete(id); toast.success('Удалено'); load(); }
    catch { toast.error('Ошибка'); }
  };

  if (loading) return <div style={{ color: 'var(--text-muted)', padding: 40 }}>Загрузка...</div>;

  return (
    <div className="animate-in">
      <div className="page-header">
        <h1 className="page-title">Точки продаж</h1>
        <p className="page-subtitle">{places.length} точек</p>
      </div>
      <div className="page-actions">
        <button className="btn btn-primary" onClick={() => { setEditingPlace(null); setShowForm(true); }}>
          <Plus size={16} /> Добавить точку
        </button>
      </div>
      <div className="table-container">
        <table className="table">
          <thead><tr><th></th><th>Название</th><th>Код</th><th>Адрес</th><th>Телефон</th><th>Статус</th><th></th></tr></thead>
          <tbody>
            {places.map(p => (
              <tr key={p.id}>
                <td><MapPin size={16} style={{ color: p.is_active ? 'var(--primary)' : 'var(--text-muted)' }} /></td>
                <td style={{ fontWeight: 600 }}>{p.name}</td>
                <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-muted)' }}>{p.code || '—'}</td>
                <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{p.address || '—'}</td>
                <td style={{ fontSize: 12 }}>{p.phone || '—'}</td>
                <td><span className={`badge ${p.is_active ? 'badge-success' : 'badge-danger'}`}>{p.is_active ? 'Активна' : 'Выкл'}</span></td>
                <td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => { setEditingPlace(p); setShowForm(true); }}><Edit2 size={14} /></button>
                    <button className="btn btn-danger btn-sm" onClick={() => handleDelete(p.id)}><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {places.length === 0 && (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
                Точек продаж нет. Создайте первую.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      {showForm && <PlaceFormModal place={editingPlace} onClose={() => { setShowForm(false); setEditingPlace(null); }} onSaved={load} />}
    </div>
  );
}